import type { StaffAttentionSummary } from "@lafayette/schemas";
import { useGetIdentity } from "@refinedev/core";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import { deepLinks } from "../navigation/deep-links";
import { offeredAreas } from "../navigation/offering";
import { useAreaHref, useOffering } from "../navigation/use-offering";
import { AsyncState } from "../ui/async-state";
import { Button } from "../ui/button";
import { Notice } from "../ui/notice";
import { dateText, greetingNameOf, greetingWord } from "./greeting";
import { HomeHero, waitingSentence } from "./HomeHero";
import { HomeLayout } from "./HomeLayout";
import {
  AreasSection,
  BlindWarning,
  InformationSection,
  RefreshLine,
  SupportSection,
} from "./HomeSections";
import { NeedsYouNow } from "./NeedsYouNow";
import { type WaitingModel, waitingModel } from "./needs-you";
import { type CreatedRequest, NewRequest } from "./new-request/NewRequest";
import { useAttentionSummary, useStaffDisplayName } from "./queries";
import { useOptionalHomeRuntime } from "./runtime";
import { statsSlot } from "./stats-slot";
import { TeamLoadSide, useRefreshTeamLoad, useShowsTeamLoad } from "./TeamLoadSide";
import { useHomeTrends, useRefreshHomeTrends } from "./trends-queries";

// Home (CHT-767, CHT-2020): the dashboard of the staff member. The page reads one closed answer (`GET /staff/attention`) and draws it: the
// greeting and how many customers wait on a person, the customers who need the staff member now, the queues that could not be read, the
// information queues, the areas of the role and the support facts of this build. It judges nothing itself and adds no rule: a count is what
// the inbox and the request queue already decide, and the API lists only the sources the role may use. The places for the stat tiles
// (CHT-2025) and for team load (CHT-2026) are `stats` and `side` of `HomeLayout`.

function Greeting({
  waiting,
  onCreated,
}: Readonly<{ waiting: WaitingModel | null; onCreated: (done: CreatedRequest) => void }>) {
  const now = useOptionalHomeRuntime()?.now?.() ?? new Date();
  // The name comes from the staff directory. The sign-in identity is the fallback, and it counts only when it is clearly a person's name:
  // the live provider puts an email address or a user id in `name`.
  const displayName = useStaffDisplayName();
  const { data: identity } = useGetIdentity<{ name?: string }>();
  const first = greetingNameOf(displayName) ?? greetingNameOf(identity?.name);
  const coverageHref = useAreaHref()("coverage", deepLinks.coverage());
  const sentence = waiting === null ? null : waitingSentence(waiting);
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
      <div className="grid min-w-0 gap-1">
        <p className="m-0 text-meta font-semibold tracking-[0.06em] text-muted-foreground uppercase">
          {dateText(now)}
        </p>
        {/* The page title for a screen reader and for the shell is "Home"; the greeting is what a person reads. */}
        <h1 id="page-title" className="sr-only">
          Home
        </h1>
        <p className="m-0 font-serif text-3xl leading-9 font-medium tracking-[-0.015em] text-balance sm:text-[40px] sm:leading-[44px]">
          {first === null ? greetingWord(now) : `${greetingWord(now)}, ${first}`}
        </p>
        {sentence === null ? null : (
          <p className="m-0 text-sm leading-6 text-muted-foreground sm:text-base">{sentence}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <NewRequest onCreated={onCreated} />
        {coverageHref === null ? null : (
          <Button asChild variant="outline" size="lg">
            <Link to={coverageHref}>View coverage</Link>
          </Button>
        )}
      </div>
    </header>
  );
}

/** After "New request": says so, with a link to the request page when the staff member may open it. Fixed words and the customer name as text. */
function CreatedNotice({ done }: Readonly<{ done: CreatedRequest }>) {
  const href = useAreaHref()("requests", deepLinks.request(done.created.requestId));
  return (
    <Notice tone="success" role="status">
      <p>
        Request created for {done.customerName}.{" "}
        {done.created.state === "ASSIGNED"
          ? "It is assigned to a concierge."
          : "No concierge could take it yet. A supervisor will assign it."}
      </p>
      {href === null ? null : (
        <Button asChild variant="link" className="px-0">
          <Link to={href}>Open the request</Link>
        </Button>
      )}
    </Notice>
  );
}

function Dashboard({
  summary,
  waiting,
  stats,
  onRefresh,
  fetching,
}: Readonly<{
  summary: StaffAttentionSummary;
  waiting: WaitingModel;
  /** The stat tiles (CHT-2025), or `null` while they load and for a role that has none. */
  stats: ReactNode;
  onRefresh: () => void;
  fetching: boolean;
}>) {
  const areaHref = useAreaHref();
  const showsTeamLoad = useShowsTeamLoad();
  const refreshTeamLoad = useRefreshTeamLoad();
  const blind = summary.sources.filter((source) => source.status === "unavailable");
  if (summary.sources.length === 0) {
    // No queue, no dark card and no list. The stat tiles of the role (an administrator's privacy tile, a catalog editor's places) stand alone.
    return (
      <>
        {stats}
        <AsyncState
          kind="empty"
          title="No attention queues for your role"
          message="Your role has no queue that needs action on this page. Open one of your areas below."
        />
      </>
    );
  }
  const startHref =
    waiting.state === "ready" && waiting.start !== null
      ? areaHref("concierge", deepLinks.conversation(waiting.start.item.id))
      : null;
  return (
    <>
      <BlindWarning blind={blind} onRefresh={onRefresh} />
      <HomeLayout
        hero={({ wide }) =>
          waiting.state === "hidden" ? null : (
            <HomeHero model={waiting} startHref={startHref} wide={wide} />
          )
        }
        needs={<NeedsYouNow summary={summary} />}
        stats={stats}
        side={showsTeamLoad ? <TeamLoadSide /> : undefined}
      />
      <InformationSection summary={summary} />
      <RefreshLine
        generatedAt={summary.generatedAt}
        fetching={fetching}
        onRefresh={() => {
          onRefresh();
          if (showsTeamLoad) refreshTeamLoad();
        }}
      />
    </>
  );
}

export function HomePage() {
  const offering = useOffering();
  const query = useAttentionSummary();
  const trends = useHomeTrends();
  const refreshTrends = useRefreshHomeTrends();
  const summary = query.data;
  const waiting = summary === undefined ? null : waitingModel(summary);
  const areas = offeredAreas(offering).filter((item) => item.key !== "home");
  const [created, setCreated] = useState<CreatedRequest | null>(null);

  return (
    <section className="grid gap-8" aria-labelledby="page-title" aria-busy={query.isFetching}>
      <Greeting
        waiting={waiting}
        onCreated={(done) => {
          setCreated(done);
          // The new request may change what needs the staff member: read the queues and the tiles again.
          void query.refetch();
          void refreshTrends();
        }}
      />
      {created === null ? null : <CreatedNotice done={created} />}

      {query.isPending ? (
        <AsyncState
          kind="loading"
          title="Reading what needs you"
          message="Reading the queues you may use."
        />
      ) : query.isError ? (
        <AsyncState
          kind="error"
          title="Home could not be loaded"
          message="The staff API did not answer. Your areas are still available in the menu."
          onRetry={() => void query.refetch()}
        />
      ) : summary === undefined || waiting === null ? null : (
        <Dashboard
          summary={summary}
          waiting={waiting}
          stats={statsSlot(trends)}
          onRefresh={() => {
            void query.refetch();
            void refreshTrends();
          }}
          fetching={query.isFetching}
        />
      )}

      <AreasSection areas={areas} />
      <SupportSection />
    </section>
  );
}
